import React, { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Search, Package, Pencil, Trash2, ArrowUpDown, AlertTriangle, TrendingUp, TrendingDown,
  Filter, X, Upload, FileSpreadsheet, Check, Loader2, Wrench, ClipboardList, ArrowRightLeft, Warehouse,
} from "lucide-react";
import VoiceInputButton from "@/components/VoiceInputButton";
import StockAdjustPopover from "@/components/StockAdjustPopover";
import BarcodeScanner from "@/components/BarcodeScanner";

const EINHEITEN = ["Stück", "Liter", "Meter", "m²", "Satz", "Paar", "kg", "Set"];

const LagerPage = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companySettings, setCompanySettings] = useState({ tax_rate: 19 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [sortField, setSortField] = useState("bezeichnung");
  const [quickFilter, setQuickFilter] = useState("all"); // 'all' | 'low' | 'reserved'
  const [typeTab, setTypeTab] = useState("all"); // 'all' | 'material' | 'dienstleistung'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    bezeichnung: "", artikelnummer: "", hersteller: "", lieferant: "", beschreibung: "",
    einkaufspreis: 0, verkaufspreis: 0, marge_prozent: 0, einheit: "Stück", bestand: 0, min_bestand: 0, kategorie: "",
    item_type: "material",
    is_order_specific: false,
  });

  // CSV Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Bulk selection + conversion state
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Confirmation for bulk conversion (replaces window.confirm which is blocked in iframe previews)
  const [bulkConfirm, setBulkConfirm] = useState({ open: false, toOrderSpecific: false, ids: [], affectedCount: 0 });

  // Auftragsartikel → Lagerartikel Konvertierungs-Dialog
  const [convertToLagerDialog, setConvertToLagerDialog] = useState({ open: false, material: null, bestand: 0, min_bestand: 0 });

  const fetchMaterials = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterSupplier) params.append("lieferant", filterSupplier);
      params.append("sort", sortField);
      const res = await api.get(`/api/materials?${params.toString()}`);
      setMaterials(res.data);
      // Refresh distinct categories from current materials
      const cats = Array.from(new Set((res.data || []).map(m => m.kategorie).filter(Boolean)));
      setCategories(cats.sort());
    } catch (err) {
      toast.error("Fehler beim Laden der Materialien");
    } finally {
      setLoading(false);
    }
  }, [search, filterSupplier, sortField]);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/api/materials/suppliers");
      setSuppliers(res.data);
    } catch (err) { /* ignore */ }
  };

  const fetchManufacturers = async () => {
    try {
      const res = await api.get("/api/materials/manufacturers");
      setManufacturers(res.data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);
  useEffect(() => {
    fetchSuppliers();
    fetchManufacturers();
    api.get("/api/company-settings")
      .then(r => setCompanySettings({ ...r.data, tax_rate: r.data?.tax_rate != null ? r.data.tax_rate : 19 }))
      .catch(() => setCompanySettings({ tax_rate: 19 }));
  }, []);

  const resetForm = () => {
    setFormData({
      bezeichnung: "", artikelnummer: "", hersteller: "", lieferant: "", beschreibung: "",
      einkaufspreis: 0, verkaufspreis: 0, marge_prozent: 0, einheit: "Stück", bestand: 0, min_bestand: 0, kategorie: "",
      item_type: typeTab === "dienstleistung" ? "dienstleistung" : "material",
      is_order_specific: typeTab === "auftragsartikel",
    });
    setEditing(null);
  };

  const handleEdit = (m) => {
    setEditing(m);
    setFormData({
      bezeichnung: m.bezeichnung, artikelnummer: m.artikelnummer, hersteller: m.hersteller || "", lieferant: m.lieferant,
      beschreibung: m.beschreibung, einkaufspreis: m.einkaufspreis, verkaufspreis: m.verkaufspreis,
      marge_prozent: m.marge_prozent || 0,
      einheit: m.einheit, bestand: m.bestand, min_bestand: m.min_bestand, kategorie: m.kategorie || "",
      item_type: m.item_type || "material",
      is_order_specific: !!m.is_order_specific,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bezeichnung.trim()) { toast.error("Bezeichnung erforderlich"); return; }
    // Warn when converting a stocked material to Auftragsartikel — inventory will be reset
    if (editing && formData.is_order_specific && !editing.is_order_specific) {
      const currentBestand = editing.bestand || 0;
      const currentReserved = editing.bestand_reserviert || 0;
      if (currentBestand > 0 || currentReserved > 0) {
        const ok = window.confirm(
          `"${editing.bezeichnung}" hat aktuell einen Bestand von ${currentBestand}` +
          (currentReserved > 0 ? ` (davon ${currentReserved} reserviert)` : "") +
          `.\n\nBeim Umwandeln in einen Auftragsartikel wird der Bestand komplett auf 0 gesetzt.\n\nFortfahren?`
        );
        if (!ok) return;
      }
    }
    try {
      if (editing) {
        await api.put(`/api/materials/${editing.id}`, formData);
        toast.success("Material aktualisiert");
      } else {
        await api.post("/api/materials", formData);
        toast.success("Material angelegt");
      }
      setDialogOpen(false);
      resetForm();
      fetchMaterials();
      fetchSuppliers();
      fetchManufacturers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Material wirklich löschen?")) return;
    try {
      await api.delete(`/api/materials/${id}`);
      toast.success("Material gelöscht");
      fetchMaterials();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  // Bulk selection helpers
  const toggleRowSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkConvert = (toOrderSpecific) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const affected = toOrderSpecific
      ? materials.filter(m => selectedIds.has(m.id) && !m.is_order_specific && ((m.bestand || 0) > 0 || (m.bestand_reserviert || 0) > 0)).length
      : 0;
    setBulkConfirm({ open: true, toOrderSpecific, ids, affectedCount: affected });
  };

  const confirmBulkConvert = async () => {
    const { toOrderSpecific, ids } = bulkConfirm;
    setBulkConfirm({ ...bulkConfirm, open: false });
    if (!ids || ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/api/materials/bulk-convert", { ids, is_order_specific: toOrderSpecific });
      const { converted, skipped, warnings } = res.data || {};
      toast.success(`${converted} Artikel umgewandelt${skipped > 0 ? ` (${skipped} übersprungen)` : ""}`);
      if (warnings && warnings.length > 0) {
        warnings.slice(0, 3).forEach(w => toast.warning(w));
        if (warnings.length > 3) toast.info(`+ ${warnings.length - 3} weitere Warnungen`);
      }
      clearSelection();
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei Umwandlung");
    } finally {
      setBulkBusy(false);
    }
  };

  // Single-item quick action: convert Auftragsartikel → Lagerartikel with startbestand
  const openConvertToLager = (m) => {
    setConvertToLagerDialog({ open: true, material: m, bestand: 0, min_bestand: 0 });
  };
  const submitConvertToLager = async () => {
    const m = convertToLagerDialog.material;
    if (!m) return;
    try {
      await api.put(`/api/materials/${m.id}`, {
        is_order_specific: false,
        bestand: parseFloat(convertToLagerDialog.bestand) || 0,
        min_bestand: parseFloat(convertToLagerDialog.min_bestand) || 0,
      });
      toast.success(`"${m.bezeichnung}" ist jetzt Lagerartikel`);
      setConvertToLagerDialog({ open: false, material: null, bestand: 0, min_bestand: 0 });
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei Konvertierung");
    }
  };

  const toggleSort = (field) => {
    setSortField(field);
  };

  // CSV Import functions
  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/api/materials/preview-csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(res.data);
      setImportMapping(res.data.suggested_mapping || {});
    } catch (err) {
      toast.error("Fehler beim Lesen der Datei");
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", importFile);
      form.append("mapping", JSON.stringify(importMapping));
      const res = await api.post("/api/materials/import-csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(`${res.data.imported} Materialien importiert!`);
      fetchMaterials();
      fetchSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportMapping({});
    setImportResult(null);
    setImporting(false);
  };

  const marge = (formData.verkaufspreis || 0) - (formData.einkaufspreis || 0);
  const taxRate = parseFloat(companySettings?.tax_rate != null ? companySettings.tax_rate : 19);
  const vkBrutto = (formData.verkaufspreis || 0) * (1 + taxRate / 100);
  const materialOnly = materials.filter(m => (m.item_type || "material") !== "dienstleistung" && !m.is_order_specific);
  const servicesOnly = materials.filter(m => m.item_type === "dienstleistung");
  const orderSpecificOnly = materials.filter(m => m.is_order_specific && m.item_type !== "dienstleistung");
  const totalValue = materialOnly.reduce((s, m) => s + (m.verkaufspreis || 0) * (m.bestand || 0), 0);
  const totalReserviert = materialOnly.reduce((s, m) => s + (m.bestand_reserviert || 0), 0);
  const lowStockCount = materialOnly.filter(m => m.min_bestand > 0 && m.bestand <= m.min_bestand).length;

  // Apply type tab first, then quick-filter on top of the search-filtered material list from the API
  const typeFiltered = typeTab === "material" ? materialOnly
    : typeTab === "dienstleistung" ? servicesOnly
    : typeTab === "auftragsartikel" ? orderSpecificOnly
    : materials;
  const displayedMaterials = quickFilter === "low"
    ? typeFiltered.filter(m => (m.item_type || "material") !== "dienstleistung" && !m.is_order_specific && m.min_bestand > 0 && m.bestand <= m.min_bestand)
    : quickFilter === "reserved"
      ? typeFiltered.filter(m => (m.bestand_reserviert || 0) > 0)
      : typeFiltered;

  const toggleQuickFilter = (key) => setQuickFilter(prev => (prev === key ? "all" : key));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="lager-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 style={{ fontFamily: "Barlow Condensed" }} className="text-3xl font-bold tracking-tight">Lagerverwaltung</h1>
          <p className="text-muted-foreground text-sm">
            {materialOnly.length} Material · {servicesOnly.length} Dienstleistungen
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) resetImport(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-csv-button">
                <Upload className="w-4 h-4" /> CSV Import
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" /> CSV/Excel Import
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* File upload */}
                {!importPreview ? (
                  <label className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer block hover:border-primary/50 transition-colors" data-testid="import-dropzone">
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">CSV-Datei auswählen</p>
                    <p className="text-sm text-muted-foreground mt-1">Lexware-Export oder ähnliche CSV/Excel-Dateien</p>
                    <input type="file" accept=".csv,.txt,.xlsx" className="hidden" onChange={handleImportFileChange} />
                  </label>
                ) : importResult ? (
                  /* Import Result */
                  <div className="space-y-3">
                    <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                      <div className="flex items-center gap-2 font-medium">
                        <Check className="w-5 h-5 text-green-600" />
                        {importResult.imported} Materialien importiert
                      </div>
                      {importResult.skipped > 0 && (
                        <p className="text-sm text-muted-foreground mt-1">{importResult.skipped} Zeilen übersprungen</p>
                      )}
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div className="text-sm text-red-600 space-y-1">
                        {importResult.errors.map((e, i) => <p key={i}>{e}</p>)}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Preview & Mapping */
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm">
                      <FileSpreadsheet className="w-4 h-4 text-primary" />
                      <span className="font-medium">{importFile?.name}</span>
                      <span className="text-muted-foreground">— {importPreview.total_count} Zeilen</span>
                    </div>
                    
                    {/* Column Mapping */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Spalten-Zuordnung:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {[
                          { key: "bezeichnung", label: "Bezeichnung *" },
                          { key: "artikelnummer", label: "Artikelnummer" },
                          { key: "hersteller", label: "Hersteller" },
                          { key: "verkaufspreis", label: "Verkaufspreis (VK)" },
                          { key: "einkaufspreis", label: "Einkaufspreis (EK)" },
                          { key: "einheit", label: "Einheit" },
                          { key: "lieferant", label: "Lieferant" },
                          { key: "beschreibung", label: "Beschreibung" },
                          { key: "bestand", label: "Bestand" },
                          { key: "item_type", label: "Typ (Material/DL)" },
                        ].map(({ key, label }) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="w-28 text-muted-foreground text-xs">{label}</span>
                            <select
                              className="flex-1 h-8 rounded-md border text-xs px-2 bg-background"
                              value={importMapping[key] || ""}
                              onChange={e => setImportMapping({ ...importMapping, [key]: e.target.value })}
                              data-testid={`mapping-${key}`}
                            >
                              <option value="">— nicht zuordnen —</option>
                              {importPreview.columns.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            {importMapping[key] && <Check className="w-3 h-3 text-green-600 flex-shrink-0" />}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Preview Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <p className="text-xs font-medium p-2 bg-muted/50">Vorschau (erste {importPreview.rows.length} Zeilen):</p>
                      <div className="overflow-x-auto max-h-48">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/30">
                            <tr>
                              {importPreview.columns.map(c => (
                                <th key={c} className="text-left p-1.5 font-medium whitespace-nowrap">{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {importPreview.rows.map((row, idx) => (
                              <tr key={idx} className="border-t">
                                {importPreview.columns.map(c => (
                                  <td key={c} className="p-1.5 whitespace-nowrap max-w-[200px] truncate">{row[c] || '-'}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {importResult ? (
                  <Button onClick={() => { setImportOpen(false); resetImport(); }} data-testid="import-close">Schließen</Button>
                ) : importPreview ? (
                  <>
                    <Button variant="outline" onClick={resetImport}>Zurück</Button>
                    <Button onClick={handleImport} disabled={importing || !importMapping.bezeichnung} className="gap-2" data-testid="import-start-button">
                      {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {importing ? "Importiere..." : `${importPreview.total_count} Artikel importieren`}
                    </Button>
                  </>
                ) : null}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-material-button">
                <Plus className="w-4 h-4" /> {typeTab === "dienstleistung" ? "Neue Dienstleistung" : "Neues Material"}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="material-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
                {editing
                  ? (formData.item_type === "dienstleistung" ? "Dienstleistung bearbeiten"
                     : formData.is_order_specific ? "Auftragsartikel bearbeiten"
                     : "Material bearbeiten")
                  : (formData.item_type === "dienstleistung" ? "Neue Dienstleistung"
                     : formData.is_order_specific ? "Neuer Auftragsartikel"
                     : "Neues Material")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Typ-Auswahl */}
              {!editing && (
                <div className="flex gap-2 p-1 bg-muted rounded-lg" data-testid="material-type-toggle">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, item_type: "material", einheit: formData.einheit === "Pauschal" ? "Stück" : formData.einheit })}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${formData.item_type === "material" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="type-toggle-material"
                  >
                    <Package className="w-4 h-4" /> Material
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, item_type: "dienstleistung", einheit: "Pauschal", bestand: 0, min_bestand: 0, is_order_specific: false })}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-1.5 rounded-md text-sm font-medium transition-colors ${formData.item_type === "dienstleistung" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="type-toggle-service"
                  >
                    <Wrench className="w-4 h-4" /> Dienstleistung
                  </button>
                </div>
              )}
              {/* Auftragsartikel-Toggle: nur bei Material sichtbar */}
              {formData.item_type !== "dienstleistung" && (
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${formData.is_order_specific ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                  data-testid="material-order-specific-toggle"
                >
                  <input
                    type="checkbox"
                    checked={!!formData.is_order_specific}
                    onChange={e => setFormData({ ...formData, is_order_specific: e.target.checked, ...(e.target.checked ? { bestand: 0, min_bestand: 0 } : {}) })}
                    className="mt-0.5 w-4 h-4"
                    data-testid="material-order-specific-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <ClipboardList className="w-4 h-4" />
                      Auftragsartikel (kein Lagerbestand)
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.is_order_specific
                        ? (editing && !editing.is_order_specific
                            ? "Beim Speichern wird der aktuelle Bestand auf 0 gesetzt und der Artikel wird künftig nicht mehr in der Bestandsführung/Inventur berücksichtigt."
                            : "Wird nicht in Bestandsführung/Inventur berücksichtigt. EK/VK/Marge werden gespeichert.")
                        : "Deaktiviert = normaler Lagerartikel mit Bestandsführung."}
                    </p>
                  </div>
                </label>
              )}
              <div>
                <Label>Bezeichnung *</Label>
                <div className="flex gap-2 items-center">
                  <Input value={formData.bezeichnung} onChange={e => setFormData({...formData, bezeichnung: e.target.value})}
                    placeholder="z.B. Ölfilter VAG 06J115561B" data-testid="material-bezeichnung" className="flex-1" />
                  <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, bezeichnung: t }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={formData.item_type === "dienstleistung" ? "col-span-2" : ""}>
                  <Label>Artikelnummer</Label>
                  <div className="flex gap-2 items-center">
                    <Input value={formData.artikelnummer} onChange={e => setFormData({...formData, artikelnummer: e.target.value})}
                      placeholder="z.B. 06J115561B" data-testid="material-artikelnummer" className="flex-1" />
                    <BarcodeScanner
                      title="Artikelnummer scannen"
                      description="Halte den Barcode oder QR-Code auf der Verpackung vor die Kamera."
                      onScan={(code) => {
                        // Check if material with this artikelnummer already exists
                        const existing = materials.find(m => (m.artikelnummer || "").trim() === code.trim());
                        if (existing && (!editing || existing.id !== editing.id)) {
                          toast.info(`Bereits im Lager: "${existing.bezeichnung}" – Du bearbeitest nun diesen Artikel.`);
                          handleEdit(existing);
                        } else {
                          setFormData(f => ({ ...f, artikelnummer: code }));
                          toast.success(`Artikelnummer übernommen: ${code}`);
                        }
                      }}
                    />
                  </div>
                </div>
                {formData.item_type !== "dienstleistung" && (
                  <>
                    <div>
                      <Label>Hersteller</Label>
                      <Input value={formData.hersteller} onChange={e => setFormData({...formData, hersteller: e.target.value})}
                        placeholder="z.B. Bosch, MANN, Mahle" data-testid="material-hersteller" list="manufacturer-suggestions" />
                      <datalist id="manufacturer-suggestions">
                        {manufacturers.map(h => <option key={h} value={h} />)}
                      </datalist>
                    </div>
                    <div className="col-span-2">
                      <Label>Lieferant</Label>
                      <Input value={formData.lieferant} onChange={e => setFormData({...formData, lieferant: e.target.value})}
                        placeholder="z.B. Stahlgruber" data-testid="material-lieferant" list="supplier-suggestions" />
                      <datalist id="supplier-suggestions">
                        {suppliers.map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                  </>
                )}
              </div>
              <div>
                <Label>Beschreibung</Label>
                <div className="flex gap-2 items-center">
                  <Input value={formData.beschreibung} onChange={e => setFormData({...formData, beschreibung: e.target.value})}
                    placeholder="Zusätzliche Details..." data-testid="material-beschreibung" className="flex-1" />
                  <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, beschreibung: f.beschreibung ? `${f.beschreibung} ${t}` : t }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div>
                  <Label>EK (€)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.einkaufspreis || ""}
                    onChange={e => {
                      const ek = parseFloat(e.target.value) || 0;
                      const mp = formData.marge_prozent || 0;
                      const vk = mp > 0 ? Math.round(ek * (1 + mp / 100) * 100) / 100 : formData.verkaufspreis;
                      setFormData({...formData, einkaufspreis: ek, verkaufspreis: vk});
                    }}
                    data-testid="material-einkaufspreis" />
                </div>
                <div>
                  <Label>Marge (%)</Label>
                  <Input type="number" step="0.1" min="0" value={formData.marge_prozent || ""}
                    onChange={e => {
                      const mp = parseFloat(e.target.value) || 0;
                      const ek = formData.einkaufspreis || 0;
                      const vk = ek > 0 ? Math.round(ek * (1 + mp / 100) * 100) / 100 : formData.verkaufspreis;
                      setFormData({...formData, marge_prozent: mp, verkaufspreis: vk});
                    }}
                    placeholder="z.B. 30"
                    data-testid="material-marge-prozent" />
                </div>
                <div>
                  <Label>VK netto (€)</Label>
                  <Input type="number" step="0.01" min="0" value={formData.verkaufspreis || ""}
                    onChange={e => {
                      const vk = parseFloat(e.target.value) || 0;
                      const ek = formData.einkaufspreis || 0;
                      const mp = ek > 0 ? Math.round((vk - ek) / ek * 10000) / 100 : 0;
                      setFormData({...formData, verkaufspreis: vk, marge_prozent: mp});
                    }}
                    data-testid="material-verkaufspreis" />
                </div>
                <div>
                  <Label>VK brutto (€)</Label>
                  <Input type="number" step="0.01" min="0" value={vkBrutto > 0 ? vkBrutto.toFixed(2) : ""}
                    onChange={e => {
                      const brutto = parseFloat(e.target.value) || 0;
                      const netto = taxRate > 0 ? Math.round(brutto / (1 + taxRate / 100) * 100) / 100 : brutto;
                      const ek = formData.einkaufspreis || 0;
                      const mp = ek > 0 ? Math.round((netto - ek) / ek * 10000) / 100 : 0;
                      setFormData({...formData, verkaufspreis: netto, marge_prozent: mp});
                    }}
                    placeholder={`inkl. ${taxRate}%`}
                    data-testid="material-verkaufspreis-brutto" />
                </div>
                <div>
                  <Label>Marge €</Label>
                  <div className={`h-10 flex items-center px-3 rounded-md border text-sm font-medium ${marge >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="material-marge-display">
                    {marge >= 0 ? '+' : ''}{marge.toFixed(2)} €
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Einheit</Label>
                  {formData.item_type === "dienstleistung" ? (
                    <div className="h-10 flex items-center px-3 rounded-md border text-sm bg-muted text-muted-foreground" data-testid="material-einheit-pauschal">
                      Pauschal
                    </div>
                  ) : (
                    <Select value={formData.einheit} onValueChange={v => setFormData({...formData, einheit: v})}>
                      <SelectTrigger data-testid="material-einheit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EINHEITEN.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {formData.item_type !== "dienstleistung" && !formData.is_order_specific && (
                  <>
                    <div>
                      <Label>Verfügbar</Label>
                      <Input type="number" step="0.01" min="0" value={formData.bestand || ""}
                        onChange={e => setFormData({...formData, bestand: parseFloat(e.target.value) || 0})}
                        data-testid="material-bestand" />
                      {editing && (editing.bestand_reserviert || 0) > 0 && (
                        <p className="text-xs text-amber-700 mt-1">
                          Reserviert: {editing.bestand_reserviert} {formData.einheit}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Mindestbestand</Label>
                      <Input type="number" step="0.01" min="0" value={formData.min_bestand || ""}
                        onChange={e => setFormData({...formData, min_bestand: parseFloat(e.target.value) || 0})}
                        data-testid="material-min-bestand" />
                    </div>
                  </>
                )}
              </div>
              <div>
                <Label>Kategorie</Label>
                <Input value={formData.kategorie} onChange={e => setFormData({...formData, kategorie: e.target.value})}
                  placeholder="z.B. Filter, Bremsen, Öle..." data-testid="material-kategorie"
                  list="category-suggestions" />
                <datalist id="category-suggestions">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Abbrechen</Button>
                <Button type="submit" data-testid="material-submit">{editing ? "Aktualisieren" : "Anlegen"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Type Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit flex-wrap" data-testid="type-tabs">
        {[
          { key: "all", label: `Alle (${materials.length})`, icon: Filter },
          { key: "material", label: `Material (${materialOnly.length})`, icon: Package },
          { key: "auftragsartikel", label: `Auftragsartikel (${orderSpecificOnly.length})`, icon: ClipboardList },
          { key: "dienstleistung", label: `Dienstleistungen (${servicesOnly.length})`, icon: Wrench },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => { setTypeTab(key); setQuickFilter("all"); clearSelection(); }}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${typeTab === key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`type-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Stats */}
      {typeTab === "dienstleistung" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Wrench className="w-4 h-4" />Dienstleistungen</div>
            <p className="text-2xl font-bold mt-1">{servicesOnly.length}</p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" />Ø VK</div>
            <p className="text-2xl font-bold mt-1">
              {(servicesOnly.length > 0 ? servicesOnly.reduce((s, m) => s + (m.verkaufspreis || 0), 0) / servicesOnly.length : 0).toFixed(2)} €
            </p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" />Höchster VK</div>
            <p className="text-2xl font-bold mt-1">
              {(servicesOnly.length > 0 ? Math.max(...servicesOnly.map(m => m.verkaufspreis || 0)) : 0).toFixed(2)} €
            </p>
          </div>
        </div>
      ) : typeTab === "auftragsartikel" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><ClipboardList className="w-4 h-4" />Auftragsartikel</div>
            <p className="text-2xl font-bold mt-1">{orderSpecificOnly.length}</p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" />Ø VK</div>
            <p className="text-2xl font-bold mt-1">
              {(orderSpecificOnly.length > 0 ? orderSpecificOnly.reduce((s, m) => s + (m.verkaufspreis || 0), 0) / orderSpecificOnly.length : 0).toFixed(2)} €
            </p>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Package className="w-4 h-4" />Mehrfach bestellt</div>
            <p className="text-2xl font-bold mt-1">{orderSpecificOnly.filter(m => (m.usage_count || 0) > 1).length}</p>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <button
          type="button"
          onClick={() => setQuickFilter("all")}
          className={`text-left border rounded-xl p-4 transition-all hover:shadow-md hover:border-primary/40 ${quickFilter === "all" ? "ring-2 ring-primary/40 bg-primary/5" : "bg-card"}`}
          data-testid="stat-all"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Package className="w-4 h-4" />Artikel gesamt</div>
          <p className="text-2xl font-bold mt-1">{materials.length}</p>
        </button>
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" />Lagerwert (VK)</div>
          <p className="text-2xl font-bold mt-1">{totalValue.toFixed(2)} €</p>
        </div>
        <button
          type="button"
          onClick={() => toggleQuickFilter("reserved")}
          className={`text-left border rounded-xl p-4 transition-all hover:shadow-md ${quickFilter === "reserved" ? "ring-2 ring-amber-400 bg-amber-100/50 border-amber-300" : totalReserviert > 0 ? "bg-amber-50 border-amber-200 hover:border-amber-400" : "bg-card hover:border-amber-300"}`}
          data-testid="stat-reserved"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Package className="w-4 h-4" />Reserviert (Aufträge)</div>
          <p className={`text-2xl font-bold mt-1 ${totalReserviert > 0 ? "text-amber-700" : ""}`}>{totalReserviert}</p>
        </button>
        <button
          type="button"
          onClick={() => toggleQuickFilter("low")}
          className={`text-left border rounded-xl p-4 transition-all hover:shadow-md ${quickFilter === "low" ? "ring-2 ring-orange-400 bg-orange-100/50 border-orange-300" : lowStockCount > 0 ? "bg-orange-50 border-orange-200 hover:border-orange-400" : "bg-card hover:border-orange-300"}`}
          data-testid="stat-low-stock"
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><AlertTriangle className="w-4 h-4" />Niedriger Bestand</div>
          <p className={`text-2xl font-bold mt-1 ${lowStockCount > 0 ? "text-orange-600" : ""}`}>{lowStockCount}</p>
        </button>
      </div>
      )}

      {quickFilter !== "all" && (
        <div className="flex items-center gap-2 text-sm bg-muted/40 px-3 py-2 rounded-lg" data-testid="active-filter-banner">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span>
            Filter aktiv: <strong>{quickFilter === "low" ? "Nur Artikel mit niedrigem Bestand" : "Nur reservierte Artikel"}</strong>
            <span className="text-muted-foreground"> · {displayedMaterials.length} {displayedMaterials.length === 1 ? "Artikel" : "Artikel"}</span>
          </span>
          <Button variant="ghost" size="sm" className="ml-auto h-7 px-2" onClick={() => setQuickFilter("all")} data-testid="clear-quick-filter">
            <X className="w-3 h-3 mr-1" />Filter entfernen
          </Button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Suchen: Bezeichnung, Artikelnr., Lieferant..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="material-search" />
        </div>
        <BarcodeScanner
          title="Artikel scannen"
          description="Scanne einen Barcode oder QR-Code, um den Artikel direkt im Lager zu finden."
          onScan={(code) => {
            const found = materials.find(m =>
              (m.artikelnummer || "").trim() === code.trim()
            );
            if (found) {
              toast.success(`Gefunden: "${found.bezeichnung}"`);
              handleEdit(found);
            } else {
              setSearch(code);
              toast.info(`Kein Artikel mit "${code}" gefunden. Suche wurde gesetzt.`);
            }
          }}
        />
        {suppliers.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterSupplier || "__all__"} onValueChange={v => setFilterSupplier(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="supplier-filter"><SelectValue placeholder="Lieferant..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle Lieferanten</SelectItem>
                {suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {filterSupplier && (
              <Button variant="ghost" size="icon" onClick={() => setFilterSupplier("")}><X className="w-4 h-4" /></Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk selection action bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border-2 border-primary bg-primary/5 shadow-sm" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2 text-sm">
            <Check className="w-4 h-4 text-primary" />
            <span><strong>{selectedIds.size}</strong> Artikel ausgewählt</span>
          </div>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button
              size="sm"
              variant="default"
              onClick={() => bulkConvert(true)}
              disabled={bulkBusy}
              className="gap-2 bg-amber-600 hover:bg-amber-700"
              data-testid="bulk-to-order-specific-btn"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
              → Auftragsartikel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkConvert(false)}
              disabled={bulkBusy}
              className="gap-2"
              data-testid="bulk-to-lager-btn"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Warehouse className="w-4 h-4" />}
              → Lagerartikel
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              disabled={bulkBusy}
              data-testid="bulk-clear-btn"
            >
              <X className="w-4 h-4" />
              Auswahl aufheben
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {typeTab !== "dienstleistung" && (
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer"
                      checked={displayedMaterials.length > 0 && displayedMaterials.filter(m => m.item_type !== "dienstleistung").every(m => selectedIds.has(m.id))}
                      onChange={e => {
                        if (e.target.checked) {
                          const next = new Set(selectedIds);
                          displayedMaterials.filter(m => m.item_type !== "dienstleistung").forEach(m => next.add(m.id));
                          setSelectedIds(next);
                        } else {
                          const next = new Set(selectedIds);
                          displayedMaterials.forEach(m => next.delete(m.id));
                          setSelectedIds(next);
                        }
                      }}
                      data-testid="bulk-select-all"
                      title="Alle angezeigten auswählen"
                    />
                  </th>
                )}
                <th className="text-left p-3 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort("bezeichnung")}>
                  <span className="flex items-center gap-1">Bezeichnung <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="text-left p-3 font-medium cursor-pointer hover:bg-muted hidden md:table-cell" onClick={() => toggleSort("artikelnummer")}>
                  <span className="flex items-center gap-1">Art.Nr. <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="text-left p-3 font-medium cursor-pointer hover:bg-muted hidden lg:table-cell" onClick={() => toggleSort("lieferant")}>
                  <span className="flex items-center gap-1">Lieferant <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="text-right p-3 font-medium">EK</th>
                <th className="text-right p-3 font-medium">VK</th>
                <th className="text-right p-3 font-medium hidden sm:table-cell">Marge %</th>
                {typeTab !== "dienstleistung" && typeTab !== "auftragsartikel" && (
                  <>
                    <th className="text-right p-3 font-medium cursor-pointer hover:bg-muted" onClick={() => toggleSort("bestand")}>
                      <span className="flex items-center gap-1 justify-end">Verfügbar <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="text-right p-3 font-medium hidden sm:table-cell">Reserviert</th>
                  </>
                )}
                <th className="text-right p-3 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {displayedMaterials.length === 0 ? (
                <tr><td colSpan={10} className="text-center p-8 text-muted-foreground">
                  {typeTab === "dienstleistung" ? "Noch keine Dienstleistungen angelegt"
                    : typeTab === "auftragsartikel" ? "Noch keine Auftragsartikel gespeichert"
                    : quickFilter === "low" ? "Alle Bestände sind ausreichend ✓"
                    : quickFilter === "reserved" ? "Aktuell sind keine Artikel reserviert"
                    : (search || filterSupplier) ? "Keine Materialien gefunden" : "Noch keine Materialien angelegt"}
                </td></tr>
              ) : (
                displayedMaterials.map(m => {
                  const isService = m.item_type === "dienstleistung";
                  const isOrderSpecific = !!m.is_order_specific && !isService;
                  const isLow = !isService && !isOrderSpecific && m.min_bestand > 0 && m.bestand <= m.min_bestand;
                  const margeVal = m.marge || (m.verkaufspreis - m.einkaufspreis);
                  const margePct = m.marge_prozent || (m.einkaufspreis > 0 ? Math.round((m.verkaufspreis - m.einkaufspreis) / m.einkaufspreis * 10000) / 100 : 0);
                  const reserviert = m.bestand_reserviert || 0;
                  return (
                    <tr key={m.id} className={`border-t hover:bg-muted/30 ${isLow ? 'bg-orange-50/50' : ''} ${selectedIds.has(m.id) ? 'bg-primary/5' : ''}`} data-testid="material-row">
                      {typeTab !== "dienstleistung" && (
                        <td className="p-3 w-10">
                          {!isService && (
                            <input
                              type="checkbox"
                              className="w-4 h-4 cursor-pointer"
                              checked={selectedIds.has(m.id)}
                              onChange={() => toggleRowSelection(m.id)}
                              data-testid={`bulk-select-row-${m.id}`}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </td>
                      )}
                      <td className="p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{m.bezeichnung}</span>
                          {m.hersteller && (
                            <span className="text-xs text-muted-foreground">· {m.hersteller}</span>
                          )}
                          {isService && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700" data-testid="service-badge">
                              <Wrench className="w-2.5 h-2.5" /> Dienstleistung
                            </span>
                          )}
                          {isOrderSpecific && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800" data-testid="order-specific-badge">
                              <ClipboardList className="w-2.5 h-2.5" /> Auftragsartikel
                            </span>
                          )}
                          {(m.usage_count || 0) > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700" title={m.last_used_at ? `Zuletzt: ${new Date(m.last_used_at).toLocaleDateString('de-DE')}` : ''}>
                              {m.usage_count}× bestellt
                            </span>
                          )}
                        </div>
                        {m.beschreibung && <div className="text-xs text-muted-foreground">{m.beschreibung}</div>}
                        <div className="text-xs text-muted-foreground md:hidden">{m.artikelnummer && `Art.Nr: ${m.artikelnummer}`}</div>
                      </td>
                      <td className="p-3 text-muted-foreground hidden md:table-cell">{m.artikelnummer || '-'}</td>
                      <td className="p-3 text-muted-foreground hidden lg:table-cell">{m.lieferant || '-'}</td>
                      <td className="p-3 text-right">{m.einkaufspreis.toFixed(2)} €</td>
                      <td className="p-3 text-right font-medium">{m.verkaufspreis.toFixed(2)} €</td>
                      <td className={`p-3 text-right hidden sm:table-cell font-medium ${margeVal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span className="flex items-center justify-end gap-1">
                          {margeVal >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {margePct.toFixed(1)}% ({margeVal.toFixed(2)} €)
                        </span>
                      </td>
                      {typeTab !== "dienstleistung" && typeTab !== "auftragsartikel" && (
                        <>
                          <td className="p-3 text-right">
                            {isService || isOrderSpecific ? (
                              <span className="text-muted-foreground text-xs">—</span>
                            ) : (
                              <div className="flex items-center gap-2 justify-end" data-testid="material-bestand-cell">
                                <span className={`inline-flex items-center gap-1 ${isLow ? 'text-orange-600 font-bold' : ''}`}>
                                  {isLow && <AlertTriangle className="w-3 h-3" />}
                                  {m.bestand} {m.einheit}
                                </span>
                                <StockAdjustPopover material={m} onAdjusted={() => fetchMaterials()} />
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right hidden sm:table-cell" data-testid="material-reserviert-cell">
                            {!isService && !isOrderSpecific && reserviert > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-medium">
                                {reserviert} {m.einheit}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                        </>
                      )}
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          {isOrderSpecific && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openConvertToLager(m)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="In Lagerartikel umwandeln"
                              data-testid={`convert-to-lager-btn-${m.id}`}
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(m)} data-testid="edit-material-btn">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(m.id)} className="text-destructive hover:text-destructive" data-testid="delete-material-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Convert Auftragsartikel → Lagerartikel Dialog */}
      <Dialog
        open={convertToLagerDialog.open}
        onOpenChange={(v) => setConvertToLagerDialog({ ...convertToLagerDialog, open: v })}
      >
        <DialogContent className="sm:max-w-[440px]" data-testid="convert-to-lager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Barlow Condensed" }}>
              <Warehouse className="w-5 h-5" />
              In Lagerartikel umwandeln
            </DialogTitle>
          </DialogHeader>
          {convertToLagerDialog.material && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border">
                <div className="font-medium text-sm">{convertToLagerDialog.material.bezeichnung}</div>
                {convertToLagerDialog.material.artikelnummer && (
                  <div className="text-xs text-muted-foreground">Art.Nr: {convertToLagerDialog.material.artikelnummer}</div>
                )}
                <div className="flex gap-4 mt-1 text-xs">
                  <span>EK: <strong>{(convertToLagerDialog.material.einkaufspreis || 0).toFixed(2)} €</strong></span>
                  <span>VK: <strong>{(convertToLagerDialog.material.verkaufspreis || 0).toFixed(2)} €</strong></span>
                  <span>Marge: <strong>{(convertToLagerDialog.material.marge_prozent || 0).toFixed(1)}%</strong></span>
                </div>
                {(convertToLagerDialog.material.usage_count || 0) > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {convertToLagerDialog.material.usage_count}× zuvor bestellt
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Preise werden übernommen. Lege den aktuellen Startbestand fest, damit Bestandsführung und Inventur greifen.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Startbestand *</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={convertToLagerDialog.bestand}
                    onChange={e => setConvertToLagerDialog({ ...convertToLagerDialog, bestand: e.target.value })}
                    placeholder="0"
                    data-testid="convert-to-lager-bestand"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Mindestbestand</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={convertToLagerDialog.min_bestand}
                    onChange={e => setConvertToLagerDialog({ ...convertToLagerDialog, min_bestand: e.target.value })}
                    placeholder="0"
                    data-testid="convert-to-lager-min-bestand"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertToLagerDialog({ open: false, material: null, bestand: 0, min_bestand: 0 })}>
              Abbrechen
            </Button>
            <Button onClick={submitConvertToLager} data-testid="convert-to-lager-submit" className="gap-2">
              <Warehouse className="w-4 h-4" />
              Als Lagerartikel speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk convert confirmation */}
      <AlertDialog
        open={bulkConfirm.open}
        onOpenChange={(v) => setBulkConfirm({ ...bulkConfirm, open: v })}
      >
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkConfirm.toOrderSpecific
                ? `${bulkConfirm.ids.length} Artikel zu Auftragsartikeln umwandeln?`
                : `${bulkConfirm.ids.length} Artikel zu Lagerartikeln umwandeln?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {bulkConfirm.toOrderSpecific ? (
                  <>
                    <p>Die ausgewählten Artikel werden künftig nicht mehr in der Bestandsführung/Inventur berücksichtigt.</p>
                    {bulkConfirm.affectedCount > 0 && (
                      <div className="p-2 rounded border border-amber-300 bg-amber-50 text-amber-800">
                        <strong>⚠️ {bulkConfirm.affectedCount}</strong> {bulkConfirm.affectedCount === 1 ? "Artikel hat" : "Artikel haben"} aktuell Bestand oder Reservierungen — dieser wird auf 0 gesetzt.
                      </div>
                    )}
                  </>
                ) : (
                  <p>Die ausgewählten Artikel werden zu normalen Lagerartikeln (Startbestand: 0). Der Bestand kann anschließend gesetzt werden.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-confirm-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkConvert}
              className={bulkConfirm.toOrderSpecific ? "bg-amber-600 hover:bg-amber-700" : ""}
              data-testid="bulk-confirm-ok"
            >
              Umwandeln
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LagerPage;
